import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { SingleSelect, MultiSelect } from 'widgets-v6/select';
import { isInternalUser } from 'states/profile/business-rules';
import { useCampaignSummary } from '../campaign-summary';
import {
    campaignFormUpdate,
    changeRevenueModel,
    closeCampaignDuplicateModal,
    duplicateCampaign,
} from './actions';
import { useCampaignDuplicateModal } from './hook';
import { useDispatch } from 'react-redux';

import {
    canViewCampaignCreator,
    getRevenueModelOptions,
    RevenueModelMapping,
} from 'states/resources/campaigns/business-logic';

const NONE_ADVERTISER = {
    label: 'Select Advertiser',
    value: '',
    revenueModel: RevenueModelMapping.Disabled,
};

const NONE_ASSIGNEE = {
    label: 'Select Assignee',
    value: '',
};

function recalculateMaxTotalSpendLocal(billings, term, rate) {
    if (term === 'billable_media_cost_markup') {
        return _.round(billings / (1 + rate), 2);
    } else {
        return _.round(billings * (1 - rate), 2);
    }
}

function getMergedClientOptions(baseOptions, currentClients, defaultLabelPrefix = 'Organization') {
    const currentClientOptions = _.map(currentClients, client => ({
        label: _.get(client, 'name') || `${defaultLabelPrefix} (${client.organization})`,
        value: client.organization,
        deprecated: _.get(client, 'deprecated', false),
    }));

    return _.chain([...baseOptions, ...currentClientOptions])
        .uniqBy('value')
        .sortBy(option => _.toLower(option.label))
        .value();
}

export const CampaignDuplicateModal = () => {
    const dispatch = useDispatch();
    const [didAutoClearAssignee, setDidAutoClearAssignee] = useState(false);
    const {
        loading,
        open,
        campaign,
        ownOrganization,
        draft,
        showErrors,
        clientGrouping,
        clientMapping,
        advertisers,
        salesReps,
        partners,
    } = useCampaignDuplicateModal();
    const { archivedAdsAndCreatives } = useCampaignSummary();

    const hasArchivedAdsOrCreatives =
        (archivedAdsAndCreatives.ads.length || archivedAdsAndCreatives.creatives.length) &&
        (archivedAdsAndCreatives.ads.some(({ isArchived }) => isArchived) ||
            archivedAdsAndCreatives.creatives.some(({ isArchived }) => isArchived));

    const currentAdvertiserClients = useMemo(
        () => _.filter(campaign.clients, { type: 'advertiser' }),
        [campaign.clients]
    );

    const currentSalesRepClients = useMemo(
        () => _.filter(campaign.clients, { type: 'sales_rep' }),
        [campaign.clients]
    );

    const currentPartnerClients = useMemo(() => _.filter(campaign.clients, { type: 'partner' }), [
        campaign.clients,
    ]);

    const advertiserOptions = useMemo(() => {
        return getMergedClientOptions(advertisers, currentAdvertiserClients, 'Advertiser');
    }, [advertisers, currentAdvertiserClients]);

    const salesRepOptions = useMemo(() => {
        return getMergedClientOptions(salesReps, currentSalesRepClients, 'Sales Rep');
    }, [salesReps, currentSalesRepClients]);

    const partnerOptions = useMemo(() => {
        return getMergedClientOptions(partners, currentPartnerClients, 'Partner');
    }, [partners, currentPartnerClients]);

    const advertisersWithCurrentMapping = useMemo(() => _.keyBy(advertiserOptions, 'value'), [
        advertiserOptions,
    ]);

    const salesRepsWithCurrentMapping = useMemo(() => _.keyBy(salesRepOptions, 'value'), [
        salesRepOptions,
    ]);

    const partnersWithCurrentMapping = useMemo(() => _.keyBy(partnerOptions, 'value'), [
        partnerOptions,
    ]);

    const assigneesMapping = useMemo(() => _.keyBy(_.get(ownOrganization, 'users', []), 'id'), [
        ownOrganization,
    ]);

    const getAdvertiserId = clients => {
        const advertiserClient = _.find(clients, { type: 'advertiser' });
        return advertiserClient ? advertiserClient.organization : '';
    };
    const getSalesRepIds = clients => {
        return _.chain(clients)
            .filter({ type: 'sales_rep' })
            .map('organization')
            .compact()
            .value();
    };

    const getPartnerIds = clients => {
        return _.chain(clients)
            .filter({ type: 'partner' })
            .map('organization')
            .compact()
            .value();
    };

    const currentAdvertiser = useMemo(() => {
        const advertiserId = getAdvertiserId(campaign.clients);
        return advertiserId ? advertisersWithCurrentMapping[advertiserId] : false;
    }, [campaign.clients, advertisersWithCurrentMapping]);

    const currentAdvertiserId = useMemo(() => getAdvertiserId(campaign.clients), [
        campaign.clients,
    ]);

    const selectedAdvertiserId = useMemo(() => getAdvertiserId(_.get(draft, 'clients', [])), [
        draft.clients,
    ]);

    const currentSalesReps = useMemo(() => {
        const salesRepIds = getSalesRepIds(campaign.clients);
        return _.compact(salesRepIds.map(id => salesRepsWithCurrentMapping[id]));
    }, [campaign.clients, salesRepsWithCurrentMapping]);

    const currentSalesRepIds = useMemo(() => getSalesRepIds(campaign.clients), [campaign.clients]);

    const selectedSalesRepIds = useMemo(() => getSalesRepIds(_.get(draft, 'clients', [])), [
        draft.clients,
    ]);

    const currentPartners = useMemo(() => {
        const partnerIds = getPartnerIds(campaign.clients);
        return _.compact(partnerIds.map(id => partnersWithCurrentMapping[id]));
    }, [campaign.clients, partnersWithCurrentMapping]);

    const currentPartnerIds = useMemo(() => getPartnerIds(campaign.clients), [campaign.clients]);

    const selectedPartnerIds = useMemo(() => getPartnerIds(_.get(draft, 'clients', [])), [
        draft.clients,
    ]);

    // Check if current advertiser is deprecated
    const currentAdvertiserIsDeprecated = useMemo(
        () => currentAdvertiser && currentAdvertiser.deprecated === true,
        [currentAdvertiser]
    );

    const newlySelectedAdvertiserIsDeprecated = useMemo(() => {
        const advertiser = selectedAdvertiserId
            ? advertisersWithCurrentMapping[selectedAdvertiserId]
            : null;
        return advertiser ? advertiser.deprecated === true : false;
    }, [selectedAdvertiserId, advertisersWithCurrentMapping]);

    const shouldBlockDeprecatedAdvertiser =
        newlySelectedAdvertiserIsDeprecated ||
        (currentAdvertiserIsDeprecated &&
            (!selectedAdvertiserId || selectedAdvertiserId === currentAdvertiserId));

    // Check if current sales rep is deprecated
    const currentSalesRepsAreDeprecated = useMemo(
        () => currentSalesReps.some(salesRep => salesRep.deprecated === true),
        [currentSalesReps]
    );

    const newlySelectedSalesRepsAreDeprecated = useMemo(
        () =>
            selectedSalesRepIds.some(
                salesRepId =>
                    salesRepsWithCurrentMapping[salesRepId] &&
                    salesRepsWithCurrentMapping[salesRepId].deprecated === true
            ),
        [selectedSalesRepIds, salesRepsWithCurrentMapping]
    );

    const shouldBlockDeprecatedSalesRep =
        newlySelectedSalesRepsAreDeprecated ||
        (currentSalesRepsAreDeprecated &&
            (_.isEmpty(selectedSalesRepIds) || _.isEqual(selectedSalesRepIds, currentSalesRepIds)));

    // Check if current partner is deprecated
    const currentPartnersAreDeprecated = useMemo(
        () => currentPartners.some(partner => partner.deprecated === true),
        [currentPartners]
    );

    const newlySelectedPartnersAreDeprecated = useMemo(
        () =>
            selectedPartnerIds.some(
                partnerId =>
                    partnersWithCurrentMapping[partnerId] &&
                    partnersWithCurrentMapping[partnerId].deprecated === true
            ),
        [selectedPartnerIds, partnersWithCurrentMapping]
    );

    const shouldBlockDeprecatedPartner =
        newlySelectedPartnersAreDeprecated ||
        (currentPartnersAreDeprecated &&
            (_.isEmpty(selectedPartnerIds) || _.isEqual(selectedPartnerIds, currentPartnerIds)));

    const currentAssigneeId = _.get(campaign, 'creatorId', '');
    const currentAssignee = currentAssigneeId ? assigneesMapping[currentAssigneeId] : null;
    const selectedAssigneeId = _.get(draft, 'creatorId', '');
    const selectedAssignee = selectedAssigneeId ? assigneesMapping[selectedAssigneeId] : null;

    const currentAssigneeIsDeleted = currentAssigneeId && !currentAssignee;

    const newlySelectedAssigneeIsDeleted = selectedAssigneeId && !selectedAssignee;

    const currentAssigneeIsDeprecated = currentAssignee && currentAssignee.deprecated === true;

    const newlySelectedAssigneeIsDeprecated =
        selectedAssignee && selectedAssignee.deprecated === true;

    const shouldBlockDeletedAssignee =
        newlySelectedAssigneeIsDeleted ||
        (currentAssigneeIsDeleted && selectedAssigneeId === currentAssigneeId);

    const shouldBlockDeprecatedAssignee =
        newlySelectedAssigneeIsDeprecated ||
        (currentAssigneeIsDeprecated &&
            (!selectedAssigneeId || selectedAssigneeId === currentAssigneeId));

    const shouldBlockEmptyAssignee = canViewCampaignCreator() && !selectedAssigneeId;

    const shouldBlockInvalidAssignee =
        shouldBlockDeprecatedAssignee || shouldBlockDeletedAssignee || shouldBlockEmptyAssignee;

    useEffect(() => {
        if (selectedAssigneeId && !selectedAssignee) {
            setDidAutoClearAssignee(true);
            dispatch(campaignFormUpdate({ creatorId: '' }));
        }
    }, [dispatch, selectedAssigneeId, selectedAssignee]);

    useEffect(() => {
        if (!open && didAutoClearAssignee) {
            setDidAutoClearAssignee(false);
        }
    }, [open, didAutoClearAssignee]);

    const updateDraft = changes => {
        dispatch(campaignFormUpdate(changes));
    };

    const onCampaignDraftChange = fieldName => fieldValue => {
        const newFormData = {
            [fieldName]: fieldValue,
        };
        updateDraft(newFormData);
    };

    const onBillingRateChange = newBillingRate => {
        const { billing_term, total_billings_local, flights, flightPacingStrategy } = campaign;

        const newFormData = {
            billing_rate: newBillingRate,
        };

        newFormData.campaign_max_total_spend_local = recalculateMaxTotalSpendLocal(
            total_billings_local,
            billing_term,
            newBillingRate
        );

        // If this campaigns has flights then we need to update the total_cost_budget
        // fields for each flight as the billing rate has change.
        if (flightPacingStrategy === 'campaign' && flights && flights.length) {
            // Only update flights which haven't ended yet.
            let totalCostBudgetForWholeCampaign = 0;
            const now = moment();
            const newFlights = flights.map(flight => {
                const flightTotalCostBudget =
                    flight.end && now.isAfter(flight.end)
                        ? flight.total_cost_budget
                        : flight.billings_local - flight.billings_local * newBillingRate;
                totalCostBudgetForWholeCampaign += flightTotalCostBudget;
                return {
                    ...flight,
                    total_cost_budget: flightTotalCostBudget,
                };
            });
            newFormData.campaign_max_total_spend_local = totalCostBudgetForWholeCampaign;
            newFormData.flights = newFlights;
        }

        updateDraft(newFormData);
    };

    const getCampaignCreatorOptions = (users, currentCreatorId) => {
        return _(users)
            .filter(
                user =>
                    currentCreatorId === user.id ||
                    (!user.suspended &&
                        !user.deprecated &&
                        user.global_role !== 'revoked' &&
                        !(_.isEmpty(user.first_name) && _.isEmpty(user.last_name)))
            )
            .map(user => ({
                label: user.first_name + ' ' + user.last_name,
                value: user.id,
            }))
            .sortBy(user => user.label)
            .value();
    };

    const handleRevenueModelChange = value => {
        dispatch(changeRevenueModel(value));
    };

    const handleClientChange = (selectedClients, clientType) => {
        const normalizedSelectedClients = _.chain(selectedClients)
            .castArray()
            .flattenDeep()
            .filter(value => value !== undefined && value !== null)
            .value();

        const draftClientsGrouping = _.chain(_.get(draft, 'clients', []))
            .groupBy('type')
            .mapValues(clients =>
                clients.map(({ organization, shared, type }) => ({
                    organization,
                    shared,
                    type,
                }))
            )
            .value();

        const groupingSource = _.isEmpty(_.get(draft, 'clients', []))
            ? clientGrouping
            : draftClientsGrouping;

        const validRevenueModels = getRevenueModelOptions(campaign).map(option => option.value);
        const selectedAdvertiserId = _.head(normalizedSelectedClients);

        if (clientType === 'advertiser') {
            const selectedAdvertiser = advertisersWithCurrentMapping[selectedAdvertiserId];

            if (selectedAdvertiser) {
                let newRevenueModel = null;
                if (!selectedAdvertiser.revenueModel) {
                    newRevenueModel = RevenueModelMapping.Disabled;
                } else if (validRevenueModels.includes(selectedAdvertiser.revenueModel)) {
                    newRevenueModel = selectedAdvertiser.revenueModel;
                }

                handleRevenueModelChange(newRevenueModel);

                if (selectedAdvertiser.revenueModel === RevenueModelMapping.AgencyMargin) {
                    onBillingRateChange(selectedAdvertiser.agencyMarginRate);
                }
            }

            if (selectedAdvertiserId === '') {
                handleRevenueModelChange(RevenueModelMapping.Disabled);
            }
        }

        const nextGrouping = {
            ...groupingSource,
            [clientType]: _.map(normalizedSelectedClients, organizationId => ({
                organization: organizationId,
                shared: _.get(clientMapping, `${organizationId}.shared`, false),
                type: clientType,
            })),
        };

        const clients = _(nextGrouping)
            .map(clients => clients)
            .flatten()
            .value()
            .filter(c => c.organization !== '');

        onCampaignDraftChange('clients')(clients);
    };

    const handleUpdateDuplicateBehaviour = value => {
        onCampaignDraftChange('duplicateBehaviour')(value);
    };

    const handleClose = () => {
        dispatch(closeCampaignDuplicateModal());
    };

    return (
        <React.Fragment>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h6">Duplicate Campaign</Typography>
                        <IconButton onClick={handleClose} disabled={loading} size="large">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 200,
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <DialogContentText>
                                The new campaign will be paused and will require new start/end
                                dates. Any scheduled reports or shared reports will also be removed.
                            </DialogContentText>
                            {hasArchivedAdsOrCreatives ? (
                                <DialogContentText>
                                    <SingleSelect
                                        required
                                        placeholder="Select a behaviour"
                                        value={draft.duplicateBehaviour}
                                        onChange={handleUpdateDuplicateBehaviour}
                                        label="This campaign contains archived ads or creatives. Include archived items in duplicated campaign?"
                                        options={[
                                            {
                                                label: 'Duplicate and unarchive items',
                                                value: 'duplicateUnarchive',
                                            },
                                            {
                                                label: 'Duplicate, but keeps items archived',
                                                value: 'duplicateKeep',
                                            },
                                            {
                                                label: 'Skip archived items',
                                                value: 'skipArchived',
                                            },
                                        ]}
                                    />
                                </DialogContentText>
                            ) : null}
                            {(currentAdvertiserIsDeprecated ||
                                currentPartnersAreDeprecated ||
                                currentSalesRepsAreDeprecated ||
                                currentAssigneeIsDeprecated ||
                                currentAssigneeIsDeleted) && (
                                <React.Fragment>
                                    {showErrors ? (
                                        <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                                            {
                                                'Error occurred while duplicating campaign. Please reach out to your dedicated Customer Engagement Specialist or Client Partner.'
                                            }
                                        </Alert>
                                    ) : (
                                        <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
                                            {
                                                'One or more selected entities are deprecated or deleted. Please select valid advertiser, partner, sales rep, and assignee below.'
                                            }
                                        </Alert>
                                    )}
                                    <Box sx={{ mb: 1, mt: 2 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                mb: 1.5,
                                                color: shouldBlockDeprecatedPartner
                                                    ? 'error.main'
                                                    : 'text.primary',
                                            }}
                                        >
                                            Partners
                                            {!isInternalUser() && ' (Read Only)'}
                                        </Typography>
                                        <Grid container alignItems="center" spacing={2}>
                                            <Grid item xs={12}>
                                                <MultiSelect
                                                    error={shouldBlockDeprecatedPartner}
                                                    errorMessage={
                                                        'One or more selected partners are deprecated'
                                                    }
                                                    value={_.map(
                                                        draft.clients.filter(
                                                            client => client.type === 'partner'
                                                        ),
                                                        c => c.organization
                                                    )}
                                                    options={[...partnerOptions]}
                                                    onChange={partners =>
                                                        handleClientChange(partners, 'partner')
                                                    }
                                                    placeholder="Partners"
                                                    disabled={!isInternalUser()}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box sx={{ mb: 1, mt: 2 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                mb: 1.5,
                                                color: shouldBlockDeprecatedAdvertiser
                                                    ? 'error.main'
                                                    : 'text.primary',
                                            }}
                                        >
                                            Advertisers
                                            {!isInternalUser() && ' (Read Only)'}
                                        </Typography>
                                        <Grid container alignItems="center" spacing={2}>
                                            <Grid item xs={12}>
                                                <SingleSelect
                                                    error={
                                                        shouldBlockDeprecatedAdvertiser
                                                            ? 'Selected advertiser is deprecated'
                                                            : undefined
                                                    }
                                                    value={
                                                        draft.clients && draft.clients.length > 0
                                                            ? _.find(draft.clients, {
                                                                  type: 'advertiser',
                                                              })?.organization || ''
                                                            : ''
                                                    }
                                                    options={[
                                                        NONE_ADVERTISER,
                                                        ...advertiserOptions,
                                                    ]}
                                                    onChange={advertiser =>
                                                        handleClientChange(
                                                            [advertiser],
                                                            'advertiser'
                                                        )
                                                    }
                                                    placeholder="Advertisers"
                                                    disabled={!isInternalUser()}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box sx={{ mb: 1, mt: 2 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                mb: 1.5,
                                                color: shouldBlockDeprecatedSalesRep
                                                    ? 'error.main'
                                                    : 'text.primary',
                                            }}
                                        >
                                            Sales Reps
                                            {!isInternalUser() && ' (Read Only)'}
                                        </Typography>
                                        <Grid container alignItems="center" spacing={2}>
                                            <Grid item xs={12}>
                                                <MultiSelect
                                                    error={shouldBlockDeprecatedSalesRep}
                                                    errorMessage={
                                                        'One or more selected sales reps are deprecated'
                                                    }
                                                    value={_.map(
                                                        draft.clients.filter(
                                                            client => client.type === 'sales_rep'
                                                        ),
                                                        c => c.organization
                                                    )}
                                                    options={[...salesRepOptions]}
                                                    onChange={salesReps =>
                                                        handleClientChange(salesReps, 'sales_rep')
                                                    }
                                                    placeholder="Sales Reps"
                                                    disabled={!isInternalUser()}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    {canViewCampaignCreator() && ownOrganization && (
                                        <Box sx={{ mb: 3 }}>
                                            {didAutoClearAssignee && (
                                                <Alert severity="info" sx={{ mb: 2 }}>
                                                    {
                                                        'The previous assignee account was not found, so Assignee was reset. Please select an assignee.'
                                                    }
                                                </Alert>
                                            )}
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    mb: 1.5,
                                                    color: shouldBlockInvalidAssignee
                                                        ? 'error.main'
                                                        : 'text.primary',
                                                }}
                                            >
                                                Assignee
                                            </Typography>
                                            <Grid container alignItems="center" spacing={2}>
                                                <Grid item xs={12}>
                                                    <SingleSelect
                                                        error={
                                                            shouldBlockEmptyAssignee
                                                                ? 'Please select an assignee'
                                                                : shouldBlockDeletedAssignee
                                                                ? 'Selected assignee account no longer exists. Please choose another assignee.'
                                                                : shouldBlockDeprecatedAssignee
                                                                ? 'Selected assignee is deprecated'
                                                                : undefined
                                                        }
                                                        value={draft.creatorId}
                                                        onChange={userId =>
                                                            onCampaignDraftChange('creatorId')(
                                                                userId
                                                            )
                                                        }
                                                        options={[
                                                            NONE_ASSIGNEE,
                                                            ...getCampaignCreatorOptions(
                                                                ownOrganization.users,
                                                                campaign.creatorId
                                                            ),
                                                        ]}
                                                        placeholder="Assignee"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </React.Fragment>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            dispatch(duplicateCampaign(campaign.id, draft));
                        }}
                        color="primary"
                        variant="contained"
                        disabled={
                            loading ||
                            (hasArchivedAdsOrCreatives && !draft.duplicateBehaviour) ||
                            shouldBlockDeprecatedAdvertiser ||
                            shouldBlockDeprecatedSalesRep ||
                            shouldBlockDeprecatedPartner ||
                            shouldBlockInvalidAssignee
                        }
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};
